import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Select } from 'components/forms/Selects'
import { cloneDeep } from 'lodash'
import moment from 'moment-timezone'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { RangeDatePicker } from 'transfix-ui/components/RangeDatePicker'
import { FormModal } from 'transfix-ui'
import { REGION_ABBREVIATIONS } from 'constants/countriesStates'
import { FORMATS } from 'constants/datetime'
import {
  costControlMessage,
  regionOptions,
  controlOptions,
  costStrategyOptions,
  ruleTypeOptions,
} from '../constants/costControlConfig'
import {
  COST_STRATEGIES,
  CONTROL_OPTIONS,
  RULE_TYPES,
  IRegionOption,
  IControlOption,
  ICostStrategyOption,
  IRuleTypeOption,
  ICostControlConfig,
} from '../types/costControlConfig'
import {
  closeAddCostControlConfigModal,
  updateCostControlConfig,
  createCostControlConfig,
} from '../utils/actions/costControlConfig'
import {
  selectCostControlConfigModalOpen,
  selectCostControlConfigModalError,
  selectCostControlConfigSaving,
  selectCostControlConfig,
} from '../utils/reducers/costControlConfig'

const defaultState: ICostControlConfig = {
  pickupRegion: REGION_ABBREVIATIONS.N,
  deliveryRegion: REGION_ABBREVIATIONS.N,
  costStrategy: COST_STRATEGIES.TCM25,
  controlOption: CONTROL_OPTIONS.quoting,
  ruleType: RULE_TYPES.lane,
  rangeStart: '',
  rangeEnd: '',
  isDrop: false,
}

export const AddCostControlConfigModal = () => {
  const dispatch = useDispatch()
  const showModal = useSelector(selectCostControlConfigModalOpen)
  const configToEdit = useSelector(selectCostControlConfig)
  const error = useSelector(selectCostControlConfigModalError)
  const saving = useSelector(selectCostControlConfigSaving)

  // Need to delay mounting the date range picker
  // until we have the correct date values in place:
  const [datePickerReady, setDatePickerReady] = useState<boolean>(false)
  const [formState, setFormState] = useState<ICostControlConfig>(defaultState)
  const {
    controlOption,
    costStrategy,
    deliveryRegion,
    pickupRegion,
    rangeEnd,
    rangeStart,
    ruleType,
  } = formState

  const updateForm = (change: Partial<ICostControlConfig>) => {
    setFormState(current => {
      const nextState = { ...current, ...change }
      return nextState
    })
  }

  useEffect(() => {
    if (showModal) {
      updateForm(configToEdit ? cloneDeep(configToEdit) : defaultState)
      // Once the form has been updated with the config to
      // edit (if necessary), the date picker is ready to be mounted:
      setDatePickerReady(true)
    } else {
      setDatePickerReady(false)
    }
  }, [showModal])

  const closeModalHandler = () => {
    dispatch(closeAddCostControlConfigModal())
  }
  const submitForm = () => {
    if (configToEdit) {
      dispatch(updateCostControlConfig(formState))
    } else {
      dispatch(createCostControlConfig(formState))
    }
  }

  const setControlOption = (opt: IControlOption) => updateForm({ controlOption: opt.value })
  const setCostStrategy = (opt: ICostStrategyOption) => updateForm({ costStrategy: opt.value })
  const setRuleType = (opt: IRuleTypeOption) => {
    if (opt.value === RULE_TYPES.baseline) {
      updateForm({
        ruleType: opt.value,
        pickupRegion: '',
        deliveryRegion: '',
        rangeStart: '',
        rangeEnd: '',
      })
    } else if (opt.value === RULE_TYPES.outbound) {
      updateForm({
        ruleType: opt.value,
        pickupRegion: pickupRegion || defaultState.pickupRegion,
        deliveryRegion: '',
      })
    } else {
      updateForm({
        ruleType: opt.value,
        pickupRegion: pickupRegion || defaultState.pickupRegion,
        deliveryRegion: deliveryRegion || defaultState.deliveryRegion,
      })
    }
  }
  const setPickupRegion = (opt: IRegionOption) => updateForm({ pickupRegion: opt.value })
  const setDeliveryRegion = (opt: IRegionOption) => updateForm({ deliveryRegion: opt.value })
  const setDate = (dateArr: Date[]) => {
    const start = dateArr[0] ? moment(dateArr[0]).format(FORMATS.yearFirst) : undefined
    const end = dateArr[1] ? moment(dateArr[1]).format(FORMATS.yearFirst) : undefined

    if (start !== rangeStart || end !== rangeEnd) {
      updateForm({
        rangeStart: start,
        rangeEnd: end,
      })
    }
  }

  const dates: Date[] = []
  const addDate = (date: string) => {
    const [startYear, startMonth, startDay] = date.split('-').map(e => Number(e))
    dates.push(new Date(startYear, startMonth - 1, startDay))
  }
  if (rangeStart) addDate(rangeStart)
  if (rangeEnd) addDate(rangeEnd)
  const yesterday = (() => {
    const date = new Date()
    date.setDate(date.getDate() - 1)
    return date
  })()

  return (
    <FormModal
      title={`${configToEdit ? 'Edit' : 'Add'} Rule`}
      size={6}
      open={showModal}
      onClose={closeModalHandler}
      dataTest='modal-add-cost-control-config'
      error={error}
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
          dataTest: 'cancel-cost-control-config-button',
          disabled: saving,
        },
        {
          label: 'Save',
          type: 'confirm',
          action: submitForm,
          dataTest: 'save-cost-control-config-button',
          disabled: saving,
        },
      ]}>
      <InlineMessage type={INLINE_MESSAGE_VARIANTS.INFO} message={costControlMessage} />
      <div className='flex mb3'>
        <div className='w-50 ma1'>
          <Select
            options={controlOptions}
            label='Quoting vs Covering'
            value={controlOption}
            onChange={setControlOption}
            dataTest='modal-control-option'
            large
          />
        </div>
        <div className='w-50 ma1'>
          <Select
            options={costStrategyOptions}
            label='Cost Strategy'
            value={costStrategy}
            onChange={setCostStrategy}
            dataTest='modal-cost-strategy'
            large
          />
        </div>
      </div>
      <div className='flex mb3'>
        <div className='w-50 ma1'>
          <Select
            options={ruleTypeOptions}
            label='Rule Type'
            value={ruleType}
            onChange={setRuleType}
            dataTest='modal-rule-type'
            large
          />
        </div>
        <div className='w-50 ma1'>
          {datePickerReady && ruleType !== RULE_TYPES.baseline && (
            <RangeDatePicker
              label='Select Date(s)'
              placeholder='Double click for single date'
              minDate={yesterday}
              dateFormat='m-d-Y'
              dates={dates}
              onDateChange={setDate}
              large
            />
          )}
        </div>
      </div>
      {ruleType !== RULE_TYPES.baseline && (
        <div className='flex'>
          <div className='w-50 ma1'>
            <Select
              options={regionOptions}
              label='Outbound (PU)'
              value={pickupRegion}
              onChange={setPickupRegion}
              dataTest='modal-pickup-region'
              large
            />
          </div>
          <div className='w-50 ma1'>
            {ruleType === RULE_TYPES.lane && (
              <Select
                options={regionOptions}
                label='Inbound (DEL)'
                value={deliveryRegion}
                onChange={setDeliveryRegion}
                dataTest='modal-delivery-region'
                large
              />
            )}
          </div>
        </div>
      )}
    </FormModal>
  )
}
